import { useEffect } from 'react';

/**
 * Profile appearance — the catalogue, and the translation from a saved theme
 * into the CSS custom properties the page renders with.
 *
 * **The catalogue lives here, not on the server.** A preset is a set of colours
 * and radii with a name on it: pure presentation, and the only consumer is the
 * screen. `api/theming.py` validates the *shape* of what comes back — enums,
 * hex patterns, ranges, image hosts — precisely so this file can stay a list
 * of nice-looking values rather than a second copy of a security boundary.
 *
 * Everything themed goes through CSS variables on one scope element rather
 * than through Tailwind classes, because these values are per-account runtime
 * data: there is no build step that could know them. `index.css` holds the
 * `.pf-*` classes that consume them, so a themed surface is one class name at
 * the call site instead of a style object rebuilt on every render.
 *
 * The defaults below are the page exactly as it looked before any of this
 * existed. An account that never opens the editor must not be able to tell
 * that the editor was built.
 */

// ── Fonts ────────────────────────────────────────────────────────────────
// Loaded from Google Fonts on demand, one stylesheet per family, only when a
// theme actually asks for it — the alternative is every page in the app paying
// for nine families it will never draw. `stack` always ends in something the
// device already has, so a blocked or slow request degrades to a readable page
// rather than an invisible one.
//
// The weight list per family is exact. The css2 endpoint 400s the *whole*
// request for a weight a family does not publish, which would take the font
// down rather than the weight.

export const FONTS = {
    inter: {
        label: 'Inter',
        query: 'Inter:wght@300;400;500;600;700;800',
        stack: "'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
        // Already in index.css for the rest of the app — never fetched twice.
        preloaded: true,
    },
    poppins: {
        label: 'Poppins',
        query: 'Poppins:wght@300;400;500;600;700;800',
        stack: "'Poppins', system-ui, sans-serif",
    },
    outfit: {
        label: 'Outfit',
        query: 'Outfit:wght@300;400;500;600;700;800',
        stack: "'Outfit', system-ui, sans-serif",
    },
    jakarta: {
        label: 'Plus Jakarta Sans',
        query: 'Plus+Jakarta+Sans:wght@300;400;500;600;700;800',
        stack: "'Plus Jakarta Sans', system-ui, sans-serif",
    },
    space: {
        label: 'Space Grotesk',
        query: 'Space+Grotesk:wght@300;400;500;600;700',
        stack: "'Space Grotesk', system-ui, sans-serif",
    },
    sora: {
        label: 'Sora',
        query: 'Sora:wght@300;400;500;600;700;800',
        stack: "'Sora', system-ui, sans-serif",
    },
    playfair: {
        label: 'Playfair Display',
        query: 'Playfair+Display:wght@400;500;600;700;800',
        stack: "'Playfair Display', Georgia, 'Times New Roman', serif",
    },
    lora: {
        label: 'Lora',
        query: 'Lora:wght@400;500;600;700',
        stack: "'Lora', Georgia, 'Times New Roman', serif",
    },
    caveat: {
        label: 'Caveat',
        query: 'Caveat:wght@400;500;600;700',
        stack: "'Caveat', 'Segoe Script', cursive",
    },
};

export const FONT_KEYS = Object.keys(FONTS);

const loaded = new Set();

/** Put a family's stylesheet in `<head>` once, if it is not already there. */
export function ensureFont(key) {
    const font = FONTS[key];
    if (!font || font.preloaded || loaded.has(key)) return;
    if (typeof document === 'undefined') return;

    loaded.add(key);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${font.query}&display=swap`;
    document.head.appendChild(link);
}

/** Load the font a theme names, and re-load if the theme changes under us. */
export function useThemeFont(theme) {
    const key = theme?.text?.font;
    useEffect(() => { ensureFont(key); }, [key]);
}

// ── Colour helpers ───────────────────────────────────────────────────────

function hexToRgb(hex) {
    let value = String(hex || '').replace('#', '').trim();
    if (value.length === 3) value = value.split('').map((c) => c + c).join('');
    if (value.length !== 6) return { r: 255, g: 255, b: 255 };
    return {
        r: parseInt(value.slice(0, 2), 16),
        g: parseInt(value.slice(2, 4), 16),
        b: parseInt(value.slice(4, 6), 16),
    };
}

export function rgba(hex, alpha) {
    const { r, g, b } = hexToRgb(hex);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Perceived lightness, 0–1. Used to decide which way contrast has to go:
 * a tint that reads as a subtle step *down* on a white card has to be a step
 * *up* on a black one, and picking one direction for both is how half the
 * themes end up with invisible dividers.
 */
export function luminance(hex) {
    const { r, g, b } = hexToRgb(hex);
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

export const isDark = (hex) => luminance(hex) < 0.5;

/** Black or white — whichever can actually be read on the given colour. */
export function readableOn(hex) {
    return isDark(hex) ? '#ffffff' : '#111827';
}

// ── The default theme ────────────────────────────────────────────────────
// Mirrors `DEFAULT_THEME` in `api/theming.py`. Both sides need a complete
// document: the server so a stored row is never half-empty, this file so the
// editor has something to render before the first save.

export const DEFAULT_THEME = {
    preset: 'classic',
    background: { type: 'solid', color: '#ffffff', color2: '#e2e8f0', angle: 160, pattern: 'none', image: null, blur: 30, dim: 30 },
    card: { style: 'solid', color: '#ffffff', opacity: 95, radius: 40, border: '#ffffff' },
    text: { font: 'inter', heading: '#1e293b', body: '#475569', muted: '#94a3b8' },
    button: { fill: 'solid', shadow: 'soft', radius: 16, color: '#ffffff', text: '#1e293b', border: '#e2e8f0' },
    accent: '#24d4be',
    accent_text: '#ffffff',
    avatar: { shape: 'circle' },
};

/**
 * Fill a possibly-partial theme out to a complete one.
 *
 * The server normalises too, but this page also renders themes that have never
 * been to the server yet — every keystroke in the editor is one. Doing it in
 * both places is what lets the live preview be the same code path as the
 * published page rather than an approximation of it.
 */
export function resolveTheme(raw) {
    const t = raw && typeof raw === 'object' ? raw : {};
    const d = DEFAULT_THEME;
    return {
        preset: t.preset || d.preset,
        background: { ...d.background, ...(t.background || {}) },
        card: { ...d.card, ...(t.card || {}) },
        text: { ...d.text, ...(t.text || {}) },
        button: { ...d.button, ...(t.button || {}) },
        accent: t.accent || d.accent,
        accent_text: t.accent_text || d.accent_text,
        avatar: { ...d.avatar, ...(t.avatar || {}) },
    };
}

// ── Templates ────────────────────────────────────────────────────────────
// Twelve complete looks, each one a full document rather than a diff, so
// picking one can never leave a leftover from the last.
//
// They exist because the controls underneath them are the part almost nobody
// wants to operate: choosing eleven colours that work together is a design
// job, and a page of sliders with no starting point is how a customisation
// feature ends up used by 3% of accounts. Every preset is also a legal manual
// state, so "start from a template and change two things" is the normal path.

const preset = (id, labelKey, theme) => ({ id, labelKey, theme: { ...theme, preset: id } });

export const PRESETS = [
    preset('classic', 'theme.presetClassic', DEFAULT_THEME),

    preset('midnight', 'theme.presetMidnight', {
        background: { type: 'gradient', color: '#0b1020', color2: '#1e1b4b', angle: 160, pattern: 'none', image: null, blur: 30, dim: 0 },
        card: { style: 'glass', color: '#0f172a', opacity: 55, radius: 36, border: '#334155' },
        text: { font: 'jakarta', heading: '#f8fafc', body: '#cbd5e1', muted: '#64748b' },
        button: { fill: 'glass', shadow: 'none', radius: 14, color: '#1e293b', text: '#f1f5f9', border: '#475569' },
        accent: '#818cf8', accent_text: '#0b1020', avatar: { shape: 'circle' },
    }),

    preset('mono', 'theme.presetMono', {
        background: { type: 'solid', color: '#ffffff', color2: '#e5e5e5', angle: 160, pattern: 'grid', image: null, blur: 30, dim: 0 },
        card: { style: 'none', color: '#ffffff', opacity: 100, radius: 0, border: '#000000' },
        text: { font: 'space', heading: '#000000', body: '#262626', muted: '#737373' },
        button: { fill: 'outline', shadow: 'none', radius: 0, color: '#ffffff', text: '#000000', border: '#000000' },
        accent: '#000000', accent_text: '#ffffff', avatar: { shape: 'square' },
    }),

    preset('sunset', 'theme.presetSunset', {
        background: { type: 'gradient', color: '#fb923c', color2: '#db2777', angle: 150, pattern: 'none', image: null, blur: 30, dim: 0 },
        card: { style: 'glass', color: '#ffffff', opacity: 25, radius: 44, border: '#ffffff' },
        text: { font: 'poppins', heading: '#ffffff', body: '#ffe4e6', muted: '#fecdd3' },
        button: { fill: 'glass', shadow: 'soft', radius: 26, color: '#ffffff', text: '#ffffff', border: '#ffffff' },
        accent: '#ffffff', accent_text: '#be185d', avatar: { shape: 'circle' },
    }),

    preset('ocean', 'theme.presetOcean', {
        background: { type: 'gradient', color: '#0ea5e9', color2: '#0f766e', angle: 170, pattern: 'none', image: null, blur: 30, dim: 0 },
        card: { style: 'glass', color: '#0c4a6e', opacity: 40, radius: 40, border: '#7dd3fc' },
        text: { font: 'outfit', heading: '#f0f9ff', body: '#bae6fd', muted: '#7dd3fc' },
        button: { fill: 'glass', shadow: 'soft', radius: 18, color: '#ffffff', text: '#f0f9ff', border: '#7dd3fc' },
        accent: '#22d3ee', accent_text: '#083344', avatar: { shape: 'circle' },
    }),

    preset('sakura', 'theme.presetSakura', {
        background: { type: 'gradient', color: '#fde2e4', color2: '#fff1e6', angle: 140, pattern: 'dots', image: null, blur: 30, dim: 0 },
        card: { style: 'solid', color: '#fffaf7', opacity: 92, radius: 44, border: '#fbcfe8' },
        text: { font: 'playfair', heading: '#6d2e46', body: '#8b5a72', muted: '#c9a0b4' },
        button: { fill: 'solid', shadow: 'soft', radius: 24, color: '#ffffff', text: '#6d2e46', border: '#fbcfe8' },
        accent: '#ec4899', accent_text: '#ffffff', avatar: { shape: 'circle' },
    }),

    preset('forest', 'theme.presetForest', {
        background: { type: 'solid', color: '#14281d', color2: '#1f3d2b', angle: 160, pattern: 'rays', image: null, blur: 30, dim: 0 },
        card: { style: 'solid', color: '#f3f0e7', opacity: 96, radius: 28, border: '#d7cfba' },
        text: { font: 'lora', heading: '#1b3a2b', body: '#3f5c4c', muted: '#8a9a90' },
        button: { fill: 'solid', shadow: 'soft', radius: 12, color: '#ffffff', text: '#1b3a2b', border: '#d7cfba' },
        accent: '#15803d', accent_text: '#ffffff', avatar: { shape: 'rounded' },
    }),

    preset('neon', 'theme.presetNeon', {
        background: { type: 'solid', color: '#09090b', color2: '#18181b', angle: 160, pattern: 'grid', image: null, blur: 30, dim: 0 },
        card: { style: 'solid', color: '#111113', opacity: 92, radius: 20, border: '#27272a' },
        text: { font: 'space', heading: '#d9f99d', body: '#d4d4d8', muted: '#71717a' },
        button: { fill: 'outline', shadow: 'hard', radius: 8, color: '#111113', text: '#d9f99d', border: '#a3e635' },
        accent: '#a3e635', accent_text: '#14532d', avatar: { shape: 'square' },
    }),

    preset('paper', 'theme.presetPaper', {
        background: { type: 'solid', color: '#f5f1e8', color2: '#e7e0d0', angle: 160, pattern: 'none', image: null, blur: 30, dim: 0 },
        card: { style: 'none', color: '#f5f1e8', opacity: 100, radius: 0, border: '#1c1917' },
        text: { font: 'lora', heading: '#1c1917', body: '#44403c', muted: '#a8a29e' },
        button: { fill: 'solid', shadow: 'hard', radius: 4, color: '#ffffff', text: '#1c1917', border: '#1c1917' },
        accent: '#b45309', accent_text: '#ffffff', avatar: { shape: 'rounded' },
    }),

    preset('bubblegum', 'theme.presetBubblegum', {
        background: { type: 'solid', color: '#c4b5fd', color2: '#a78bfa', angle: 160, pattern: 'stripes', image: null, blur: 30, dim: 0 },
        card: { style: 'solid', color: '#ffffff', opacity: 100, radius: 28, border: '#1e1b4b' },
        text: { font: 'poppins', heading: '#1e1b4b', body: '#4c1d95', muted: '#8b5cf6' },
        button: { fill: 'solid', shadow: 'hard', radius: 18, color: '#fef08a', text: '#1e1b4b', border: '#1e1b4b' },
        accent: '#7c3aed', accent_text: '#ffffff', avatar: { shape: 'rounded' },
    }),

    preset('slate', 'theme.presetSlate', {
        background: { type: 'gradient', color: '#f8fafc', color2: '#e2e8f0', angle: 180, pattern: 'none', image: null, blur: 30, dim: 0 },
        card: { style: 'solid', color: '#ffffff', opacity: 100, radius: 16, border: '#e2e8f0' },
        text: { font: 'jakarta', heading: '#0f172a', body: '#475569', muted: '#94a3b8' },
        button: { fill: 'outline', shadow: 'none', radius: 10, color: '#ffffff', text: '#0f172a', border: '#cbd5e1' },
        accent: '#2563eb', accent_text: '#ffffff', avatar: { shape: 'rounded' },
    }),

    preset('gold', 'theme.presetGold', {
        background: { type: 'solid', color: '#0a0a0a', color2: '#1c1917', angle: 160, pattern: 'rays', image: null, blur: 30, dim: 0 },
        card: { style: 'glass', color: '#171717', opacity: 65, radius: 32, border: '#a16207' },
        text: { font: 'playfair', heading: '#fef3c7', body: '#d6d3d1', muted: '#78716c' },
        button: { fill: 'outline', shadow: 'none', radius: 6, color: '#171717', text: '#fde68a', border: '#a16207' },
        accent: '#d4af37', accent_text: '#0a0a0a', avatar: { shape: 'circle' },
    }),
];

/**
 * Apply a template without throwing away the wallpaper.
 *
 * An uploaded photo is the one part of a theme the account cannot get back by
 * clicking again, so browsing templates keeps it. The *type* only survives if
 * the image was actually being shown — otherwise picking a gradient template
 * while an unused old upload sat in the record would show the photo instead of
 * the gradient that was just chosen.
 */
export function applyPreset(presetId, current) {
    const found = PRESETS.find((p) => p.id === presetId);
    if (!found) return resolveTheme(current);

    const now = resolveTheme(current);
    const next = resolveTheme(found.theme);
    const keepImage = now.background.image;
    const wasShowingImage = now.background.type === 'image' && Boolean(keepImage);

    return {
        ...next,
        background: {
            ...next.background,
            image: keepImage || null,
            type: wasShowingImage ? 'image' : next.background.type,
            blur: wasShowingImage ? now.background.blur : next.background.blur,
            dim: wasShowingImage ? now.background.dim : next.background.dim,
        },
    };
}

// ── Wallpaper patterns ───────────────────────────────────────────────────
// Drawn in CSS rather than shipped as images: four files that have to load
// over a phone connection before the page looks finished, for something a
// repeating gradient does exactly. The ink follows the wallpaper's own
// lightness, so a pattern is never invisible on its own background.

export const PATTERNS = ['none', 'dots', 'grid', 'stripes', 'rays'];

function patternStyle(pattern, baseColor) {
    const ink = isDark(baseColor) ? 'rgba(255,255,255,0.10)' : 'rgba(15,23,42,0.08)';
    switch (pattern) {
        case 'dots':
            return { backgroundImage: `radial-gradient(${ink} 1.5px, transparent 1.5px)`, backgroundSize: '20px 20px' };
        case 'grid':
            return {
                backgroundImage: `linear-gradient(${ink} 1px, transparent 1px), linear-gradient(90deg, ${ink} 1px, transparent 1px)`,
                backgroundSize: '32px 32px',
            };
        case 'stripes':
            return { backgroundImage: `repeating-linear-gradient(45deg, ${ink} 0 10px, transparent 10px 20px)` };
        case 'rays':
            return { backgroundImage: `repeating-conic-gradient(from 0deg at 50% 0%, ${ink} 0deg 6deg, transparent 6deg 18deg)` };
        default:
            return null;
    }
}

// ── Theme → CSS variables ────────────────────────────────────────────────

const SHADOWS = {
    none: { rest: 'none', hover: 'none' },
    soft: { rest: '0 2px 10px rgba(15,23,42,0.08)', hover: '0 10px 24px rgba(15,23,42,0.16)' },
};

const AVATAR_RADIUS = { circle: '9999px', rounded: '28%', square: '12%' };

/**
 * The style object for the scope element. Everything themed on the page reads
 * from these, so a surface is a class name (`pf-card`, `pf-row`) rather than
 * an inline style rebuilt on every render.
 */
export function themeVars(rawTheme) {
    const t = resolveTheme(rawTheme);
    const font = FONTS[t.text.font] || FONTS.inter;

    const cardTransparent = t.card.style === 'none';
    const cardBg = cardTransparent ? 'transparent' : rgba(t.card.color, t.card.opacity / 100);
    // The surface whose lightness everything else has to contrast against. With
    // no card, that is the wallpaper — otherwise a "no card" theme computes its
    // dividers against a card that is not being drawn.
    const surface = cardTransparent ? t.background.color : t.card.color;
    const surfaceDark = isDark(surface);

    const fill = t.button.fill;
    const buttonBg = fill === 'outline' ? 'transparent'
        : fill === 'glass' ? rgba(t.button.color, 0.2)
            : t.button.color;

    const shadow = t.button.shadow === 'hard'
        // The offset block is the whole point of this style, so it takes its
        // colour from the border it echoes rather than from a grey.
        ? { rest: `4px 4px 0 0 ${t.button.border}`, hover: `6px 6px 0 0 ${t.button.border}` }
        : SHADOWS[t.button.shadow] || SHADOWS.none;

    return {
        // Only the family is themed, never the size. Every text size on the
        // page is a Tailwind `rem` class, and `rem` resolves against the root
        // element — so a `font-size` set here would be inherited by the handful
        // of elements with no size class and ignored by all the rest, which is
        // worse than not scaling at all.
        '--pf-font': font.stack,

        '--pf-heading': t.text.heading,
        '--pf-body': t.text.body,
        '--pf-muted': t.text.muted,
        '--pf-accent': t.accent,
        '--pf-accent-text': t.accent_text,

        '--pf-card-bg': cardBg,
        '--pf-card-border': cardTransparent ? 'transparent' : rgba(t.card.border, 0.55),
        '--pf-card-radius': `${t.card.radius}px`,
        '--pf-card-shadow': cardTransparent ? 'none' : '0 25px 50px -12px rgba(0,0,0,0.25)',
        '--pf-card-blur': t.card.style === 'glass' ? 'blur(20px)' : cardTransparent ? 'none' : 'blur(8px)',

        '--pf-btn-bg': buttonBg,
        '--pf-btn-text': t.button.text,
        '--pf-btn-text-soft': rgba(t.button.text, 0.62),
        '--pf-btn-border': fill === 'glass' ? rgba(t.button.border, 0.5) : t.button.border,
        '--pf-btn-border-w': fill === 'outline' ? '2px' : '1px',
        '--pf-btn-radius': `${t.button.radius}px`,
        '--pf-btn-shadow': shadow.rest,
        '--pf-btn-shadow-hover': shadow.hover,
        '--pf-btn-blur': fill === 'glass' ? 'blur(10px)' : 'none',

        // Tints for the small surfaces that are not buttons — the bio box, the
        // metric tiles, the country/tag chips. One step away from the card,
        // in whichever direction is actually visible on it.
        '--pf-soft-bg': surfaceDark ? 'rgba(255,255,255,0.07)' : 'rgba(15,23,42,0.04)',
        '--pf-line': surfaceDark ? 'rgba(255,255,255,0.14)' : 'rgba(15,23,42,0.09)',

        // Widgets that carry their own slate palette (rate card, track record,
        // gallery) cannot be recoloured from here, so on a dark card they are
        // given a light panel to sit on instead of being left unreadable. On a
        // light card the panel is nothing at all, which is how those blocks
        // have always looked.
        '--pf-embed-bg': surfaceDark ? '#ffffff' : 'transparent',
        '--pf-embed-pad': surfaceDark ? '0.75rem' : '0',
        '--pf-embed-radius': surfaceDark ? '1rem' : '0',

        '--pf-avatar-radius': AVATAR_RADIUS[t.avatar.shape] || AVATAR_RADIUS.circle,
    };
}

/**
 * The wallpaper: base colour or gradient, the photo over it, the pattern over
 * that, and the dim on top.
 *
 * Separate layers rather than one stacked `background` shorthand because the
 * blur has to apply to the photo *only* — the single-element version blurred
 * the dim as well, which is why the old page could never go darker than about
 * 40% no matter what was asked for.
 */
export function ProfileBackdrop({ theme, hidden = false, inline = false, className = '' }) {
    const t = resolveTheme(theme);
    if (hidden) return null;

    // `inline` fills its positioned parent instead of the viewport — the
    // editor's live preview is the same component as the published page, so
    // what it shows cannot drift from what gets shipped.
    const position = inline ? 'absolute inset-0' : 'fixed inset-0 -z-10';

    const base = t.background.type === 'gradient'
        ? `linear-gradient(${t.background.angle}deg, ${t.background.color}, ${t.background.color2})`
        : t.background.color;

    const showImage = t.background.type === 'image' && Boolean(t.background.image);
    const pattern = patternStyle(t.background.pattern, t.background.color);

    return (
        <div className={`${position} overflow-hidden ${className}`} style={{ background: base }}>
            {showImage && (
                <div
                    className="absolute inset-0"
                    style={{
                        backgroundImage: `url(${t.background.image})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        filter: t.background.blur > 0 ? `blur(${t.background.blur}px)` : 'none',
                        // A blurred layer samples past its own edge, so without
                        // the overscan the wallpaper fades out at the frame.
                        transform: t.background.blur > 0 ? 'scale(1.12)' : 'none',
                    }}
                />
            )}
            {pattern && <div className="absolute inset-0" style={pattern} />}
            {t.background.dim > 0 && (
                <div className="absolute inset-0" style={{ background: `rgba(0,0,0,${t.background.dim / 100})` }} />
            )}
        </div>
    );
}

export default PRESETS;
